

import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar'
import { useMemberRenewalStatus } from '@/features/membership/renewal'
import { cn } from '@/lib/utils'
import { IconRefresh } from '@tabler/icons-react'
import { Link, useLocation, useNavigate } from '@tanstack/react-router'
import type { LucideIcon } from 'lucide-react'

export function NavMain({
  items,
}: {
  items: {
    title: string
    url: string
    icon?: LucideIcon
  }[]
}) {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { isMobile, setOpenMobile } = useSidebar()
  const renewalStatus = useMemberRenewalStatus()

  const closeSidebarOnMobile = () => {
    if (isMobile) {
      setOpenMobile(false)
    }
  }

  const handleQuickCreateClick = () => {
    if (renewalStatus.isLoading || !renewalStatus.canRenew) {
      return
    }

    closeSidebarOnMobile()
    navigate({ to: '/membership/renew' })
  }

  const quickCreateDisabled = renewalStatus.isLoading || !renewalStatus.canRenew
  const quickCreateTooltip = renewalStatus.isLoading
    ? 'Checking your membership status…'
    : renewalStatus.reason ?? 'Submit a renewal request.'

  return (
    <SidebarGroup>
      <SidebarGroupContent className="flex flex-col gap-2">
        <SidebarMenu>
          <SidebarMenuItem className="flex items-center gap-2">
            <SidebarMenuButton
              tooltip={quickCreateTooltip}
              className={cn(
                'min-w-8 duration-200 ease-linear text-primary-foreground',
                'bg-primary active:bg-primary/90 hover:cursor-pointer',
              )}
              onClick={handleQuickCreateClick}
              disabled={quickCreateDisabled}
              title={quickCreateTooltip}
            >
              <IconRefresh className="size-4" />
              <span>Renew Membership</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        <SidebarMenu>
          {items.map(item => (
            <SidebarMenuItem key={item.title}>
              <Link
                to={item.url}
                onClick={() => {
                  closeSidebarOnMobile()
                }}
              >
                <SidebarMenuButton isActive={pathname === item.url} tooltip={item.title}>
                  {item.icon && <item.icon />}
                  <span>{item.title}</span>
                </SidebarMenuButton>
              </Link>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  )
}
